import { ORGANIZATION, PRODUCT } from "@nwga/contracts/organization";
import { COUNTIES, REGION_NAME } from "@nwga/contracts/region";
import { Button } from "@nwga/ui/components/button";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BriefcaseIcon, BuildingIcon, SproutIcon } from "lucide-react";
import { Container } from "../components/container.tsx";
import { PageHero } from "../components/page-hero.tsx";

/**
 * The front door.
 *
 * `/` was a redirect for as long as the board was the only thing here: the
 * Authority's site is the home page, and a second landing page introducing the
 * same organisation to somebody who had just arrived from it was noise.
 *
 * It is a page again because the redirect answered the wrong question. Most
 * people reach a listing from a search engine, not from the Jobs tab, and the
 * only thing on this origin that could tell them what NWGA Connect *is* was the
 * footer. So this stays deliberately short — it says what the platform does,
 * who runs it, that it is free, and then gets out of the way. Anything
 * explaining the Authority itself belongs on northwestgeorgia.us; see
 * [ARCHITECTURE.md](../../../../ARCHITECTURE.md).
 */
export const Route = createFileRoute("/")({ component: HomePage });

const COUNTY_LIST = COUNTIES.map((county) => county.name).join(", ");

/**
 * What the platform offers, in the order it currently offers it.
 *
 * The third card is not filler. Job listings are the first service here rather
 * than the whole of it, and saying so is what stops the next one — a directory,
 * a training calendar — reading like a change of purpose.
 */
const OFFERINGS = [
	{
		icon: BriefcaseIcon,
		title: "Find work nearby",
		body: `Every opening on the board was posted by the employer itself, and applications go straight to them. No account, no fee, no middleman.`,
	},
	{
		icon: BuildingIcon,
		title: "Hire from your community",
		body: `Register your business once, and post as many openings as you have. A member of the Authority reviews new businesses — usually within two business days — and then your listings are live.`,
	},
	{
		icon: SproutIcon,
		title: "More to come",
		body: `Job listings are the first service on ${PRODUCT.name}, not the last. As the Authority brings more of its work for local employers online, it will appear here.`,
	},
] as const;

function HomePage() {
	return (
		<>
			<PageHero
				size="lg"
				eyebrow={`A free service of the ${ORGANIZATION.shortName}`}
				title={PRODUCT.tagline}
				description={`${PRODUCT.name} connects people in ${REGION_NAME} with the employers already here. It is run by the Joint Development Authority and costs nothing — to browse, to apply, or to post.`}
			>
				{/*
          Painted from the band tokens rather than a button variant. Every
          variant is defined against `--background`/`--primary`, and `--primary`
          is the same navy as the band in light mode — a default button here
          would be navy on navy. `--brand` and `--band-foreground` are the two
          colours guaranteed to read against this band in both themes.
        */}
				<Button
					asChild
					size="lg"
					className="bg-brand text-brand-foreground hover:bg-brand/90"
				>
					<Link to="/jobs">Browse jobs</Link>
				</Button>
				<Button
					asChild
					size="lg"
					variant="outline"
					className="border-band-foreground/35 bg-transparent text-band-foreground hover:bg-band-foreground/10 hover:text-band-foreground dark:border-band-foreground/35 dark:bg-transparent dark:hover:bg-band-foreground/10"
				>
					<Link to="/businesses/register">Register your business</Link>
				</Button>
			</PageHero>

			<Container className="py-16">
				<ul className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
					{OFFERINGS.map((offering) => (
						<li key={offering.title}>
							<offering.icon className="size-6 text-brand" aria-hidden="true" />
							<h2 className="mt-4 font-heading font-semibold text-xl tracking-tight">
								{offering.title}
							</h2>
							<p className="mt-2 text-muted-foreground leading-relaxed">
								{offering.body}
							</p>
						</li>
					))}
				</ul>

				<p className="mt-14 border-t border-border pt-8 text-muted-foreground">
					Serving {COUNTY_LIST} counties.{" "}
					<Link
						to="/about"
						className="font-medium text-foreground underline-offset-4 hover:underline"
					>
						More about this platform
					</Link>
					.
				</p>
			</Container>
		</>
	);
}
